import { dateToGregorian } from "@/lib/gregorian";
import {
  ApiError,
  ForbiddenError,
  PaymentRequiredError,
  RateLimitedError,
  UnauthorizedError,
  ValidationError,
  classify401,
} from "./errors";
import type { Envelope, ListQuery, Page, ValidationErrors } from "./types";

/**
 * The one HTTP client. Three things live here and nowhere else:
 * envelope unwrapping, Gregorian time in query strings, and the 402
 * `accept_charges` handshake. Paths are same-origin by default: crossbar
 * under `/v2`, and the provisioner under `/provision/api/v1` (`base` in
 * the request options), which speaks the same envelope with the same
 * token. A portal session carries the cluster's API origin instead (the
 * console is served from the portal's side, not the cluster's), and both
 * prefixes hang off it: the same nginx fronts crossbar and the
 * provisioner in a deployed cluster.
 */

const BASE = "/v2";
const UI_METADATA = { ui: "switchboard", version: __CONSOLE_VERSION__ };

let tokenProvider: () => string | undefined = () => undefined;
let originProvider: () => string | undefined = () => undefined;
let onUnauthorized: (err: UnauthorizedError) => void = () => {};

export function configureClient(opts: { token: () => string | undefined; apiOrigin?: () => string | undefined; onUnauthorized?: (err: UnauthorizedError) => void }) {
  tokenProvider = opts.token;
  if (opts.apiOrigin) originProvider = opts.apiOrigin;
  if (opts.onUnauthorized) onUnauthorized = opts.onUnauthorized;
}

/** The origin every path hangs off: the session's when it has one, else this page's. */
export function apiOrigin(): string {
  return (originProvider() ?? "").replace(/\/+$/, "");
}

export interface RequestOptions {
  method?: "GET" | "PUT" | "POST" | "PATCH" | "DELETE";
  /** Goes under `data` in the envelope unless `raw` is set. */
  body?: unknown;
  /** Send the body as-is (multipart, csv, base64). */
  raw?: BodyInit;
  query?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  /** Resend with `accept_charges: true` after a 402 quote. */
  acceptCharges?: boolean;
  /** Envelope-level `action` (impersonate_user, refresh_token, ...). */
  action?: string;
  /** Skip the token (login, recovery, about). */
  anonymous?: boolean;
  signal?: AbortSignal;
  /** Return the whole envelope instead of `data`. */
  envelope?: boolean;
  /** Skip the `ui_metadata` stamp: documents whose every top-level key is data (system configs, storage plans). */
  noStamp?: boolean;
  /** API root the path hangs off. Defaults to crossbar's `/v2`; the provisioner uses `/provision/api/v1`. */
  base?: string;
}

/** The URL a path resolves to: the session's API origin, the base, the path, the query. */
export function buildUrl(path: string, query?: RequestOptions["query"], base: string = BASE): string {
  const root = `${apiOrigin()}${base}`;
  const url = path.startsWith("/") ? `${root}${path}` : `${root}/${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== "") params.set(k, String(v));
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

function stampMetadata(body: unknown, method: string): unknown {
  if (!body || typeof body !== "object" || Array.isArray(body)) return body;
  if (method !== "PUT" && method !== "POST") return body;
  // Merge rather than replace: monster-ui keys on `ui_metadata.origin`
  // (e.g. "voip") to recognise documents it manages, and both UIs coexist.
  const existing = (body as { ui_metadata?: Record<string, unknown> }).ui_metadata;
  return { ...(body as object), ui_metadata: { ...(existing ?? {}), ...UI_METADATA } };
}

export async function request<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json", ...opts.headers };
  const token = opts.anonymous ? undefined : tokenProvider();
  if (token) headers["X-Auth-Token"] = token;

  let body: BodyInit | undefined;
  if (opts.raw !== undefined) {
    body = opts.raw;
  } else if (opts.body !== undefined || opts.action || opts.acceptCharges) {
    headers["Content-Type"] = "application/json";
    const envelope: Record<string, unknown> = { data: opts.noStamp ? (opts.body ?? {}) : stampMetadata(opts.body ?? {}, method) };
    if (opts.action) envelope.action = opts.action;
    if (opts.acceptCharges) envelope.accept_charges = true;
    body = JSON.stringify(envelope);
  }

  const res = await fetch(buildUrl(path, opts.query, opts.base), { method, headers, body, signal: opts.signal });
  const text = await res.text();
  let parsed: Envelope<unknown> | undefined;
  if (text) {
    try {
      parsed = JSON.parse(text) as Envelope<unknown>;
    } catch {
      parsed = undefined;
    }
  }

  if (!res.ok) throw toError(res.status, parsed, opts.anonymous);
  if (parsed && parsed.status !== "success") throw new ApiError(parsed.message ?? "request failed", res.status, parsed);
  if (opts.envelope) return parsed as T;
  return (parsed?.data ?? (text as unknown)) as T;
}

function toError(status: number, body: Envelope<unknown> | undefined, anonymous?: boolean): ApiError {
  switch (status) {
    case 400:
      return new ValidationError((body ?? { data: {}, status: "error", request_id: "" }) as Envelope<ValidationErrors>);
    case 401: {
      const err = new UnauthorizedError(body, classify401(body));
      // A request that carried a token cannot be a login attempt, so any 401
      // other than an MFA challenge means the token is dead. Crossbar reports
      // an expired JWT with the same "invalid_credentials" envelope as a bad
      // password, so the classifier alone cannot tell the two apart.
      if (!anonymous && err.kind !== "mfa_required") onUnauthorized(err);
      return err;
    }
    case 402:
      return new PaymentRequiredError(body ?? { data: {}, status: "error", request_id: "" });
    case 403:
      return new ForbiddenError(body);
    case 429:
      return new RateLimitedError(body);
    default:
      return new ApiError(body?.message ?? `HTTP ${status}`, status, body);
  }
}

/**
 * Paginated list: translates ListQuery into crossbar's query string and
 * unwraps paging keys. Extra `query` keys in `opts` (a ratedeck, a view
 * name, a CDR document filter) ride along with the paging ones, and they
 * are spread first: a raw key that collides with one `q` owns — `page_size`,
 * `start_key`, `paginate`, `descending`, `created_from`, `created_to`,
 * `fields`, or a `filter_*` from `q.filters` — is overridden by it rather
 * than winning, so paging cannot be broken from a caller's `query`.
 */
export async function list<T>(path: string, q: ListQuery = {}, opts: Omit<RequestOptions, "envelope"> = {}): Promise<Page<T>> {
  const query: RequestOptions["query"] = {
    ...opts.query,
    page_size: q.pageSize,
    start_key: q.startKey,
    paginate: q.paginate === false ? "false" : undefined,
    descending: q.descending === undefined ? undefined : q.descending ? "true" : "false",
    created_from: q.createdFrom ? dateToGregorian(q.createdFrom) : undefined,
    created_to: q.createdTo ? dateToGregorian(q.createdTo) : undefined,
    fields: q.fields ? JSON.stringify(q.fields) : undefined,
  };
  for (const [k, v] of Object.entries(q.filters ?? {})) query[`filter_${k}`] = String(v);
  const env = await request<Envelope<T[]>>(path, { ...opts, query, envelope: true });
  return { items: env.data ?? [], pageSize: env.page_size, startKey: env.start_key, nextStartKey: env.next_start_key };
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: "GET" }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>(path, { ...opts, method: "PUT", body }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>(path, { ...opts, method: "POST", body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>(path, { ...opts, method: "PATCH", body }),
  delete: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: "DELETE" }),
  list,
};

/**
 * URL for a media element or download link. Browsers cannot set headers on
 * <audio>/<a>, so crossbar accepts the token as a query parameter here.
 */
export function authedUrl(path: string, query?: RequestOptions["query"]): string {
  return buildUrl(path, { ...query, auth_token: tokenProvider() });
}

/**
 * Upload a binary body with progress. XHR because fetch has no upload
 * progress; errors go through the same classifier as request().
 */
export function upload(path: string, file: Blob, opts: { contentType?: string; onProgress?: (fraction: number) => void; signal?: AbortSignal } = {}): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", buildUrl(path));
    xhr.setRequestHeader("Accept", "application/json");
    xhr.setRequestHeader("Content-Type", opts.contentType ?? file.type ?? "application/octet-stream");
    const token = tokenProvider();
    if (token) xhr.setRequestHeader("X-Auth-Token", token);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && opts.onProgress) opts.onProgress(e.loaded / e.total);
    };
    xhr.onerror = () => reject(new ApiError("upload failed", 0));
    xhr.onabort = () => reject(new Error("cancelled"));
    xhr.onload = () => {
      let parsed: Envelope<unknown> | undefined;
      try {
        parsed = xhr.responseText ? (JSON.parse(xhr.responseText) as Envelope<unknown>) : undefined;
      } catch {
        parsed = undefined;
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(toError(xhr.status, parsed));
    };
    opts.signal?.addEventListener("abort", () => xhr.abort());
    xhr.send(file);
  });
}

/** Path helper: `acct(id, "devices")` gives `/accounts/{id}/devices`. */
export function acct(accountId: string, ...segments: (string | number)[]): string {
  return ["/accounts", accountId, ...segments.map(String)].join("/");
}
